// client/src/components/TasksList/TasksList.container.tsx
import { useEffect, useContext, useState } from "react";
import { MemoizedTasksList as TasksList } from "./TasksList.component";
import { TasksContext } from "../../context/tasks/store";
import { allCoffrets, COFFRET } from "../../api/coffrets";
import { allData, DATA } from "../../api/dataApi";

const EMPTY_DATA: DATA = {
  numerosCoffret: [],
  numerosFacture: [],
  numerosOrdre: [],
  maxOrder: 0,
};

export const TasksListContainer = () => {
  const { coffrets, setCoffrets, search, setData } = useContext(TasksContext);
  const [page, setPage] = useState(1);
  const [filteredCoffrets, setFilteredCoffrets] = useState<COFFRET[]>([]);

  // charge + écoute les refresh (create/update/delete)
  useEffect(() => {
    const load = async () => {
      try {
        const res = await allCoffrets();
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
        setCoffrets(Array.isArray(list) ? list : []);
      } catch {
        setCoffrets([]);
      }
      try {
        const res = await allData();
        const d = (res?.data?.data ?? res?.data ?? EMPTY_DATA) as Partial<DATA>;
        setData({
          numerosCoffret: Array.isArray(d.numerosCoffret) ? d.numerosCoffret : [],
          numerosFacture: Array.isArray(d.numerosFacture) ? d.numerosFacture : [],
          numerosOrdre: Array.isArray(d.numerosOrdre) ? d.numerosOrdre : [],
          maxOrder:
            typeof d.maxOrder === "number" && Number.isFinite(d.maxOrder) ? d.maxOrder : 0,
        });
      } catch {
        setData(EMPTY_DATA);
      }
    };

    load();

    const onRefresh = () => load();
    const onStorage = (e: StorageEvent) => {
      if (e.key === "coffrets:refresh-ts") load();
    };

    window.addEventListener("coffrets:refresh", onRefresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("coffrets:refresh", onRefresh);
      window.removeEventListener("storage", onStorage);
    };
  }, [setCoffrets, setData]);

  // filtre + reset page
  useEffect(() => {
    const {
      genre,
      espece,
      type,
      faune,
      emplacement,
      numeroCoffret,
      numeroFacture,
      numeroOrdre,
    } = search;

    let list: COFFRET[] = Array.isArray(coffrets) ? [...coffrets] : [];

    const up = (s?: string) => (s ?? "").toUpperCase();
    const trim = (s?: string | number) => String(s ?? "").trim();

    if (genre) list = list.filter((c) => up(c?.genre).includes(up(genre)));
    if (espece) list = list.filter((c) => up(c?.espece).includes(up(espece)));
    if (type) list = list.filter((c) => up(c?.type) === up(type));
    if (faune) list = list.filter((c) => up(c?.faune) === up(faune));
    if (emplacement) list = list.filter((c) => up(c?.emplacement) === up(emplacement));
    if (numeroCoffret) list = list.filter((c) => trim(c?.numeroCoffret) === trim(numeroCoffret));

    // ✅ N° FACTURE : “SANS” => match champs vides/absents
    const nfSearch = trim(numeroFacture);
    if (nfSearch) {
      const wantsSans = nfSearch.toUpperCase() === "SANS";
      if (wantsSans) {
        list = list.filter((c) => trim(c?.numeroFacture) === "");
      } else {
        list = list.filter(
          (c) => up(trim(c?.numeroFacture)) === up(nfSearch)
        );
      }
    }

    if (numeroOrdre) {
      const n = parseInt(String(numeroOrdre), 10);
      if (!Number.isNaN(n)) list = list.filter((c) => c?.numeroOrdre === n);
    }

    setFilteredCoffrets(list);
    setPage(1);
  }, [search, coffrets]);

  if (!Array.isArray(coffrets) || !coffrets.length) return null;

  return (
    <TasksList
      filteredCoffrets={filteredCoffrets}
      page={page}
      setPage={setPage}
      pageSize={60}
    />
  );
};